// PUBLIC, no-login storefront configurator. Loaded inside a Shopify iframe.
// Reads ?product=<id>, fetches the public config, and lets the customer swap
// the product's actual fabrics/finishes on the 3D model (visualize only).
import { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment } from '@react-three/drei';
import {
  loadModelFromUrl, LoadedModel, buildTextureMaterial, buildColorMaterial,
  FABRIC_CHANNEL_IDS, labelForChannel, normalizeConfiguratorModels,
} from '../components/configurator/core';

const useQuery = () => new URLSearchParams(window.location.search);

// Shown at the bottom of the options column. Edit this string to change wording.
const DISCLAIMER_TEXT =
  'Colors and textures shown are a digital representation for visualization only ' +
  'and may differ from the actual product material. Pattern scale and repeat are ' +
  'approximate — the true size, direction and placement of a pattern on the finished ' +
  'piece may differ from what is shown. Final colors and patterns should be confirmed ' +
  'against a physical sample before ordering.';

export default function EmbedConfigurator() {
  const query = useQuery();
  const productId = query.get('product');
  const storeId = query.get('store');
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const [config, setConfig] = useState(null);
  // Base options the customer can switch between (each its own model + mappings).
  const baseModels = useMemo(() => normalizeConfiguratorModels(config), [config]);
  const [activeBaseIdx, setActiveBaseIdx] = useState(0);
  const [scene, setScene] = useState(null);
  const sceneCacheRef = useRef({}); // { [url]: scene }
  // selection: { [channelId]: colorObject } — persists across base switches.
  const [selection, setSelection] = useState({});
  // Active type/group tab per channel, so the swatch list isn't one long scroll.
  const [typeTab, setTypeTab] = useState({}); // { [channelId]: typeName }
  // Active fabric grade filter per channel ('All' or a grade name).
  const [gradeTab, setGradeTab] = useState({}); // { [channelId]: grade }
  // Which part sections are expanded. Closed by default so the customer sees
  // every part at a glance, then opens just the one they want.
  const [expanded, setExpanded] = useState({}); // { [channelId]: bool }
  // "Same as another part" links: { [channelId]: sourceChannelId }. A linked
  // part mirrors the source's material and hides its own swatches.
  const [mirror, setMirror] = useState({});
  const loadedRef = useRef(false);

  const activeBase = baseModels[activeBaseIdx] || baseModels[0] || null;
  const activeMappings = activeBase?.mappings || {};
  const labels = config?.labels || {};
  const channelOptions = config?.channelOptions || {};

  // Quote form
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteState, setQuoteState] = useState('idle'); // idle | submitting | done | error
  const [quoteError, setQuoteError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', quantity: '', message: '' });
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Which channels the ACTIVE base actually uses (from its part mapping).
  const usedChannels = useMemo(() => {
    const set = new Set(Object.values(activeMappings).filter((c) => c && c !== 'ignore'));
    return Array.from(set);
  }, [activeMappings]);

  // "Same as" helpers. A part can mirror another USED part of the same kind
  // (fabric↔fabric, finish↔finish). Following the chain gives the effective pick.
  const kindOf = (ch) => (FABRIC_CHANNEL_IDS.includes(ch) ? 'fabric' : 'finish');
  const resolveSel = (ch) => {
    let cur = ch;
    for (let i = 0; i < 8 && mirror[cur]; i += 1) cur = mirror[cur];
    return selection[cur];
  };
  const mirrorTargetsFor = (ch) =>
    usedChannels.filter((c) => c !== ch && kindOf(c) === kindOf(ch) && mirror[c] !== ch);

  // Per-channel allow-list of option ids (empty/absent => all allowed).
  const allowedIdsFor = (channel) => {
    const opt = channelOptions[channel];
    if (!opt) return null;
    const ids = FABRIC_CHANNEL_IDS.includes(channel) ? opt.fabricPatternIds : opt.finishTypeIds;
    return Array.isArray(ids) && ids.length > 0 ? new Set(ids) : null;
  };

  // Grouped, hierarchical option trees (mirrors the dashboard structure).
  //  - Fabrics: Type → Pattern → colors
  //  - Finishes: Group → Type → colors
  const fabricGrouped = useMemo(() => {
    if (!config) return [];
    const byType = new Map();
    for (const p of config.fabrics || []) {
      const t = p.typeName || 'Fabric';
      if (!byType.has(t)) byType.set(t, []);
      byType.get(t).push({
        patternId: p.patternId,
        patternName: p.patternName,
        grade: p.grade || null,
        colors: (p.colors || []).map((c) => ({ ...c, option: p.patternName, label: `${p.patternName} · ${c.name}`, kind: 'fabric' })),
      });
    }
    const byName = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    return Array.from(byType, ([type, patterns]) => ({
      type,
      patterns: patterns.slice().sort((a, b) => byName(a.patternName, b.patternName)),
    })).sort((a, b) => byName(a.type, b.type));
  }, [config]);

  const finishGrouped = useMemo(() => {
    if (!config) return [];
    const byGroup = new Map();
    for (const t of config.finishes || []) {
      const g = t.groupName || 'Finishes';
      if (!byGroup.has(g)) byGroup.set(g, []);
      byGroup.get(g).push({
        typeId: t.typeId,
        typeName: t.typeName,
        colors: (t.colors || []).map((c) => ({ ...c, option: t.typeName, label: `${t.typeName} · ${c.name}`, kind: 'finish' })),
      });
    }
    const byName = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    return Array.from(byGroup, ([group, types]) => ({
      group,
      types: types.slice().sort((a, b) => byName(a.typeName, b.typeName)),
    })).sort((a, b) => byName(a.group, b.group));
  }, [config]);

  // Filter the grouped fabric/finish tree to a channel's allowed option ids.
  const fabricTreeFor = (channel) => {
    const allowed = allowedIdsFor(channel);
    if (!allowed) return fabricGrouped;
    return fabricGrouped
      .map(({ type, patterns }) => ({ type, patterns: patterns.filter((p) => allowed.has(p.patternId)) }))
      .filter((g) => g.patterns.length > 0);
  };
  const finishTreeFor = (channel) => {
    const allowed = allowedIdsFor(channel);
    if (!allowed) return finishGrouped;
    return finishGrouped
      .map(({ group, types }) => ({ group, types: types.filter((t) => allowed.has(t.typeId)) }))
      .filter((g) => g.types.length > 0);
  };

  // Fetch the public config (no auth) and load the first base's model.
  useEffect(() => {
    if (!productId) { setStatus('error'); setErrorMsg('No product specified.'); return; }
    let cancelled = false;
    (async () => {
      try {
        const qs = storeId ? `?store=${encodeURIComponent(storeId)}` : '';
        const res = await fetch(`/api/public/configurator/${productId}${qs}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'This product has no 3D configurator.' : 'Failed to load.');
        const data = await res.json();
        if (cancelled) return;
        setConfig(data);
        const models = normalizeConfiguratorModels(data);
        const first = models[0];
        if (!first) throw new Error('This product has no 3D model.');
        const { scene: loaded } = await loadModelFromUrl(first.url, first.format);
        if (cancelled) return;
        sceneCacheRef.current[first.url] = loaded;
        setScene(loaded);
        setActiveBaseIdx(0);

        // Default each used channel to its first ALLOWED swatch.
        const initial = {};
        const usedSet = new Set(Object.values(first.mappings || {}).filter((c) => c && c !== 'ignore'));
        const opts = data.channelOptions || {};
        const fab = (data.fabrics || []).flatMap((p) => (p.colors || []).map((c) => ({ ...c, patternId: p.patternId, kind: 'fabric', option: p.patternName, label: `${p.patternName} · ${c.name}` })));
        const fin = (data.finishes || []).flatMap((t) => (t.colors || []).map((c) => ({ ...c, typeId: t.typeId, kind: 'finish', option: t.typeName, label: `${t.typeName} · ${c.name}` })));
        usedSet.forEach((ch) => {
          const isFab = FABRIC_CHANNEL_IDS.includes(ch);
          const allowIds = isFab ? opts[ch]?.fabricPatternIds : opts[ch]?.finishTypeIds;
          const allow = Array.isArray(allowIds) && allowIds.length > 0 ? new Set(allowIds) : null;
          const pool = (isFab ? fab : fin).filter((s) => !allow || allow.has(isFab ? s.patternId : s.typeId));
          if (pool.length) initial[ch] = pool[0];
        });
        setSelection(initial);
        setStatus('ready');
        loadedRef.current = true;
      } catch (err) {
        if (!cancelled) { setStatus('error'); setErrorMsg(err.message || 'Failed to load.'); }
      }
    })();
    return () => { cancelled = true; };
  }, [productId, storeId]);

  // Switch the displayed model when the customer picks a different base option.
  useEffect(() => {
    if (!loadedRef.current || !activeBase?.url) return;
    const url = activeBase.url;
    const cached = sceneCacheRef.current[url];
    if (cached) { setScene(cached); return; }
    let cancelled = false;
    loadModelFromUrl(url, activeBase.format)
      .then(({ scene: loaded }) => {
        if (cancelled) return;
        sceneCacheRef.current[url] = loaded;
        setScene(loaded);
      })
      .catch(() => { /* keep prior scene on failure */ });
    return () => { cancelled = true; };
  }, [activeBaseIdx, activeBase?.url, activeBase?.format]);

  // Build THREE materials per used channel from its EFFECTIVE selection (which
  // follows any "same as" link), so mirrored parts render the source's material.
  const channelMaterials = useMemo(() => {
    const buildFor = (channel, color) => {
      if (!color) return null;
      if (color.textureUrl) return buildTextureMaterial(color.textureUrl, { name: `ch-${channel}` });
      if (color.colorCode) {
        const metalness = channel === 'metal' ? 0.85 : 0.1;
        const roughness = channel === 'metal' ? 0.35 : 0.7;
        return buildColorMaterial(color.colorCode, { metalness, roughness, name: `ch-${channel}` });
      }
      return null;
    };
    const out = {};
    for (const channel of usedChannels) {
      let src = channel;
      for (let i = 0; i < 8 && mirror[src]; i += 1) src = mirror[src];
      const mat = buildFor(channel, selection[src]);
      if (mat) out[channel] = mat;
    }
    return out;
  }, [selection, mirror, usedChannels]);

  // Human-readable list of the customer's current picks (respecting "same as").
  const currentSelections = () =>
    usedChannels
      .filter((ch) => resolveSel(ch))
      .map((ch) => {
        const s = resolveSel(ch);
        return { label: labelForChannel(ch, labels), choice: s.label || s.name || '' };
      });

  // Structured { group, option, value } list for the parent storefront.
  const structuredSelections = () =>
    usedChannels
      .filter((ch) => resolveSel(ch))
      .map((ch) => {
        const s = resolveSel(ch);
        return { group: labelForChannel(ch, labels), option: s.option || '', value: s.name || '' };
      });

  // Notify the embedding storefront so it can prefill its own quote form.
  const postQuoteToParent = () => {
    if (typeof window === 'undefined') return;
    const selections = structuredSelections();
    const payload = {
      source: 'tcpllc-configurator',
      type: 'request-quote',
      version: 1,
      product: { id: String(productId || ''), name: config?.product?.title || '', sku: config?.product?.sku || '' },
      base: activeBase?.label || '',
      selections,
      summary: [
        activeBase?.label ? `Base: ${activeBase.label}` : '',
        ...selections.map((s) => `${s.group}: ${s.option} · ${s.value}`),
      ].filter(Boolean).join('\n'),
      configUrl: window.location.href,
    };
    // Prefer an explicit parent origin (passed as ?parentOrigin=) for security;
    // fall back to "*" so it still works if not provided.
    const parentOrigin = new URLSearchParams(window.location.search).get('parentOrigin');
    try { window.parent.postMessage(payload, parentOrigin || '*'); } catch { /* cross-origin */ }
  };

  // Are we embedded in another page (iframe)?
  const isEmbedded = typeof window !== 'undefined' && window.parent && window.parent !== window;

  const handleRequestQuote = () => {
    postQuoteToParent();
    // When embedded, the storefront handles the form. Standalone (opened
    // directly), fall back to our own quote form so the button still works.
    if (!isEmbedded) {
      setQuoteState('idle');
      setQuoteError('');
      setQuoteOpen(true);
    }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setQuoteError('Please enter your name and email.'); return; }
    setQuoteState('submitting'); setQuoteError('');
    try {
      const res = await fetch('/api/public/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: Number(productId), selections: currentSelections(), ...form }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Could not send your request.');
      }
      setQuoteState('done');
    } catch (err) {
      setQuoteState('error'); setQuoteError(err.message || 'Could not send your request.');
    }
  };

  if (status === 'loading') {
    return (
      <Centered>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading 3D model…</Typography>
      </Centered>
    );
  }
  if (status === 'error') {
    return (
      <Centered>
        <Typography variant="h6" color="text.secondary">{errorMsg}</Typography>
      </Centered>
    );
  }

  return (
    <Box sx={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, bgcolor: '#f4f6fb' }}>
      {/* Left column — 3D model */}
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, height: { xs: '55vh', md: 'auto' } }}>
        <Canvas shadows flat camera={{ position: [3, 2.5, 4], fov: 42 }} gl={{ antialias: true }} style={{ background: '#f4f6fb' }}>
          {/* `flat` disables filmic tone-mapping and the lights are kept low so the
              rendered fabric/finish color matches the swatch thumbnail instead of
              washing out lighter under heavy studio lighting. */}
          <Environment preset="studio" environmentIntensity={0.45} />
          <ambientLight intensity={0.25} />
          <directionalLight position={[5, 8, 5]} intensity={0.65} castShadow shadow-mapSize-width={2048} shadow-mapSize-height={2048} />
          <directionalLight position={[-5, 3, -5]} intensity={0.25} />
          <LoadedModel scene={scene} mappings={activeMappings} channelMaterials={channelMaterials} />
          <OrbitControls enablePan={false} minDistance={2} maxDistance={10} />
        </Canvas>
      </Box>

      {/* Right column — material pickers */}
      <Box sx={{
        bgcolor: '#fff', p: 2,
        width: { xs: '100%', md: 380 }, flexShrink: 0,
        borderLeft: { md: '1px solid #e6e8ef' }, borderTop: { xs: '1px solid #e6e8ef', md: 'none' },
        overflowY: 'auto', maxHeight: { xs: '45vh', md: '100vh' },
        boxShadow: { xs: '0 -4px 16px rgba(0,0,0,0.06)', md: '-4px 0 16px rgba(0,0,0,0.04)' },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{config.product.title}</Typography>
            {config.product.sku && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                SKU: {config.product.sku}
              </Typography>
            )}
          </Box>
          <Button variant="contained" onClick={handleRequestQuote} sx={{ borderRadius: 99, textTransform: 'none', flexShrink: 0 }}>
            Request a Quote
          </Button>
        </Box>

        {/* Base option picker — only when there's more than one base to choose. */}
        {baseModels.length > 1 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 1 }}>
              Base
              {activeBase?.label && <> — <b style={{ color: '#333' }}>{activeBase.label}</b></>}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {baseModels.map((b, i) => (
                <Box
                  key={i}
                  onClick={() => setActiveBaseIdx(i)}
                  sx={{
                    px: 1.5, py: 0.75, borderRadius: 1.5, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                    border: '2px solid', borderColor: i === activeBaseIdx ? 'primary.main' : '#dcdfe8',
                    color: i === activeBaseIdx ? 'primary.main' : 'text.primary',
                    bgcolor: i === activeBaseIdx ? 'rgba(25,118,210,0.06)' : '#fff',
                  }}
                >
                  {b.label || `Option ${i + 1}`}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {usedChannels.length === 0 && (
          <Typography variant="body2" color="text.secondary">No customizable parts on this model.</Typography>
        )}
        {usedChannels.map((channel) => {
          const isFabric = FABRIC_CHANNEL_IDS.includes(channel);
          const fabTree = isFabric ? fabricTreeFor(channel) : [];
          const finTree = isFabric ? [] : finishTreeFor(channel);
          if ((isFabric ? fabTree : finTree).length === 0) return null;
          const selectedId = selection[channel]?.id;

          // Tab per type (fabric: Fabric/Vinyl/Velvet) or group (finishes), so
          // only one type's swatches show at a time instead of a long scroll.
          const tabs = isFabric ? fabTree.map((g) => g.type) : finTree.map((g) => g.group);
          const activeTab = tabs.includes(typeTab[channel]) ? typeTab[channel] : tabs[0];
          const activeGroup = isFabric
            ? fabTree.find((g) => g.type === activeTab)
            : finTree.find((g) => g.group === activeTab);
          // Fabrics are shown as squares, finishes as circles (matches the live site).
          const swatchRadius = isFabric ? 1 : '50%';
          const renderSwatch = (sw) => (
            <Box
              key={`${channel}-${sw.id}`}
              onClick={() => setSelection((s) => ({ ...s, [channel]: sw }))}
              title={sw.label}
              sx={{
                width: 40, height: 40, borderRadius: swatchRadius, cursor: 'pointer', flexShrink: 0,
                border: '3px solid', borderColor: selectedId === sw.id ? 'primary.main' : 'transparent',
                boxShadow: '0 0 0 1px #dcdfe8',
                backgroundImage: sw.textureUrl ? `url(${sw.textureUrl})` : 'none',
                backgroundColor: sw.textureUrl ? 'transparent' : (sw.colorCode || '#ccc'),
                backgroundSize: 'cover', backgroundPosition: 'center',
              }}
            />
          );
          // Fabric grade filter (Grade 1/2/3…) within the active type.
          const grades = isFabric
            ? Array.from(new Set((activeGroup?.patterns || []).map((p) => p.grade).filter(Boolean)))
                .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
            : [];
          const activeGrade = grades.includes(gradeTab[channel]) ? gradeTab[channel] : 'All';
          const subGroups = isFabric
            ? (activeGroup?.patterns || [])
                .filter((p) => activeGrade === 'All' || p.grade === activeGrade)
                .map((p) => ({ name: p.grade ? `${p.patternName} · ${p.grade}` : p.patternName, colors: p.colors }))
            : (activeGroup?.types || []).map((t) => ({ name: t.typeName, colors: t.colors }));
          const isOpen = !!expanded[channel];
          return (
            <Box key={channel} sx={{ borderTop: '1px solid #eee', py: 0.5 }}>
              {/* Collapsible part header — click to expand/collapse */}
              <Box
                onClick={() => setExpanded((s) => ({ ...s, [channel]: !s[channel] }))}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', py: 1, gap: 1 }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', minWidth: 0 }}>
                  {labelForChannel(channel, labels)}
                  {resolveSel(channel) && <> — <b style={{ color: '#333' }}>{resolveSel(channel).label || resolveSel(channel).name}</b></>}
                </Typography>
                <Box
                  aria-hidden
                  sx={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: '50%', border: '1px solid #cfd4df',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.1rem', lineHeight: 1, color: 'text.secondary', fontWeight: 600,
                  }}
                >
                  {isOpen ? '−' : '+'}
                </Box>
              </Box>

              {isOpen && (
                <Box sx={{ pb: 1.5 }}>
                  {/* "Same as another part" — mirror this part's material to another. */}
                  {mirrorTargetsFor(channel).length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.5 }}>SAME AS</Typography>
                      {[null, ...mirrorTargetsFor(channel)].map((t) => {
                        const active = (mirror[channel] || null) === t;
                        return (
                          <Box
                            key={`${channel}-same-${t || 'sep'}`}
                            onClick={() => setMirror((m) => { const n = { ...m }; if (t) n[channel] = t; else delete n[channel]; return n; })}
                            sx={{
                              px: 1.25, py: 0.4, borderRadius: 99, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                              border: '1px solid', borderColor: active ? 'primary.main' : '#dcdfe8',
                              color: active ? 'primary.main' : 'text.secondary',
                              bgcolor: active ? 'rgba(25,118,210,0.06)' : '#fff',
                            }}
                          >
                            {t ? labelForChannel(t, labels) : 'Separate'}
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {mirror[channel] ? (
                    <Typography variant="body2" color="text.secondary">
                      Uses the same as <b style={{ color: '#333' }}>{labelForChannel(mirror[channel], labels)}</b>
                      {resolveSel(channel) && <> — {resolveSel(channel).label || resolveSel(channel).name}</>}
                    </Typography>
                  ) : (<>
                  {/* Type/group tabs */}
                  {tabs.length > 1 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
                      {tabs.map((t) => (
                        <Box
                          key={`${channel}-tab-${t}`}
                          onClick={() => setTypeTab((s) => ({ ...s, [channel]: t }))}
                          sx={{
                            px: 1.5, py: 0.5, borderRadius: 99, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                            border: '1px solid', borderColor: t === activeTab ? 'primary.main' : '#dcdfe8',
                            color: t === activeTab ? 'primary.main' : 'text.secondary',
                            bgcolor: t === activeTab ? 'rgba(25,118,210,0.06)' : '#fff',
                          }}
                        >
                          {t}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Fabric grade filter */}
                  {grades.length > 1 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.5 }}>GRADE</Typography>
                      {['All', ...grades].map((g) => (
                        <Box
                          key={`${channel}-grade-${g}`}
                          onClick={() => setGradeTab((s) => ({ ...s, [channel]: g }))}
                          sx={{
                            px: 1.25, py: 0.4, borderRadius: 99, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                            border: '1px solid', borderColor: g === activeGrade ? 'primary.main' : '#dcdfe8',
                            color: g === activeGrade ? 'primary.main' : 'text.secondary',
                            bgcolor: g === activeGrade ? 'rgba(25,118,210,0.06)' : '#fff',
                          }}
                        >
                          {g}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {subGroups.map((sg) => (
                    <Box key={`${channel}-sg-${sg.name}`} sx={{ mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.4 }}>{sg.name}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>{sg.colors.map(renderSwatch)}</Box>
                    </Box>
                  ))}
                  </>)}
                </Box>
              )}
            </Box>
          );
        })}

        {/* Disclaimer */}
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 2, pt: 2, borderTop: '1px solid #eee', lineHeight: 1.5 }}>
          {DISCLAIMER_TEXT}
        </Typography>
      </Box>

      {/* Quote modal */}
      {quoteOpen && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box onClick={() => setQuoteOpen(false)} sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.5)' }} />
          <Box sx={{ position: 'relative', bgcolor: '#fff', borderRadius: 3, width: 'min(480px, 92vw)', maxHeight: '90vh', overflowY: 'auto', p: 3, boxShadow: 24 }}>
            {quoteState === 'done' ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Request sent ✓</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Thanks! We've received your configuration and will get back to you with a quote.
                </Typography>
                <Button variant="contained" onClick={() => setQuoteOpen(false)} sx={{ borderRadius: 99, textTransform: 'none' }}>Done</Button>
              </Box>
            ) : (
              <form onSubmit={submitQuote}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Request a Quote</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  We'll include your exact configuration below.
                </Typography>

                {/* Selected configuration summary */}
                <Box sx={{ bgcolor: '#f4f6fb', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>Your configuration</Typography>
                  {currentSelections().length === 0 && <Typography variant="body2" color="text.secondary">—</Typography>}
                  {currentSelections().map((s) => (
                    <Typography key={s.label} variant="body2"><b>{s.label}:</b> {s.choice}</Typography>
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField size="small" label="Name" required value={form.name} onChange={setField('name')} />
                  <TextField size="small" label="Email" type="email" required value={form.email} onChange={setField('email')} />
                  <TextField size="small" label="Phone" value={form.phone} onChange={setField('phone')} />
                  <TextField size="small" label="Company" value={form.company} onChange={setField('company')} />
                  <TextField size="small" label="Quantity" value={form.quantity} onChange={setField('quantity')} />
                </Box>
                <TextField size="small" label="Message" multiline rows={3} fullWidth value={form.message} onChange={setField('message')} sx={{ mt: 1.5 }} />

                {quoteError && <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>{quoteError}</Typography>}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                  <Button onClick={() => setQuoteOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                  <Button type="submit" variant="contained" disabled={quoteState === 'submitting'} sx={{ borderRadius: 99, textTransform: 'none' }}>
                    {quoteState === 'submitting' ? 'Sending…' : 'Send request'}
                  </Button>
                </Box>
              </form>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}

const Centered = ({ children }) => (
  <Box sx={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f4f6fb' }}>
    {children}
  </Box>
);
